import { Injectable, Inject, signal, computed } from '@angular/core';
import { BehaviorSubject, Subject, Observable, map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { Activity } from '@go-modules/interfaces/activity';
import { Task } from '@go-modules/interfaces/task';
import { activityToken } from 'go-modules/models/activity/activity.factory';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';

/**
 * When in edit mode, provides a cloned "working copy" of the activity.
 * that components can modify. On save, updates the original. On cancel,
 * discards changes and reverts to the original.
 */
@Injectable({
	providedIn: 'root'
})
export class ActivityEditModeService {
	private isEditMode$ = signal<boolean>(false);
	private originalActivity = signal<Activity | null>(null);
	private editedActivity = signal<Activity | null>(null);
	private originalTasks = signal<Task[]>([]);
	private editedTasks = signal<Task[]>([]);
	private hasChanges = signal<boolean>(false);

	private isSaving$$ = new BehaviorSubject<boolean>(false);
	private changesReverted$$ = new Subject<void>();
	private presentTaskAdditionalSettingsComponent: any = null;

	private selectedActivity = toSignal(
		this.selectedService.selectedSubject.asObservable().pipe(
			map((selected) => selected.activity)
		),
		{ initialValue: null as Activity | null }
	);

	public currentTasks = computed(() => {
		if (this.isEditMode$()) {
			return this.editedTasks();
		}
		return this.originalTasks();
	});

	public currentActivity = computed(() => {
		if (this.isEditMode$()) {
			return this.editedActivity();
		}
		return this.selectedActivity();
	});

	public get isEditMode () {
		return this.isEditMode$();
	}

	public get hasUnsavedChanges () {
		return this.hasChanges();
	}

	public get isSaving () {
		return this.isSaving$$.value;
	}

	public get isSaving$ () {
		return this.isSaving$$.asObservable();
	}

	public get changesReverted$ () {
		return this.changesReverted$$;
	}

	constructor (
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(activityToken) private activityModel,
		private dialog: MatDialog,
		private translate: TranslateService
	) {}

	public getCurrentActivity (): Activity | null {
		return this.currentActivity();
	}

	public getCurrentTasks (): Task[] {
		return this.currentTasks();
	}

	/**
	 * Returns a computed signal for a specific task that automatically updates
	 * when the task changes or when switching between edit/non-edit mode
	 */
	public getEditableTask (taskId: number) {
		return computed(() => {
			const tasks = this.currentTasks();
			return tasks.find((t) => t.id === taskId);
		});
	}

	public getEditedTasks (): Task[] {
		return this.editedTasks();
	}

	public getEditedActivity (): Activity | null {
		return this.editedActivity();
	}

	/**
	 * Set the current tasks (should be called when tasks are loaded from the API)
	 * This keeps the service in sync with the latest task data.
	 * Only updates if not in edit mode (don't override the working copy).
	 */
	public setOriginalTasks (tasks: Task[]): void {
		if (!this.isEditMode) {
			this.originalTasks.set(tasks);
		}
	}

	public enterEditMode (tasks: Task[]): void {
		if (this.isEditMode) {
			return;
		}

		const activity = this.selectedService.getActivity();
		if (!activity) {
			throw new Error('Cannot enter edit mode: no activity selected');
		}

		this.originalActivity.set(activity);
		this.editedActivity.set(structuredClone(activity));
		this.originalTasks.set(structuredClone(tasks));
		this.editedTasks.set(structuredClone(tasks));

		this.isEditMode$.set(true);
		this.hasChanges.set(false);
	}

	/**
	 * Exit edit mode without saving changes
	 */
	public exitEditMode (): void {
		this.isEditMode$.set(false);
		this.originalActivity.set(null);
		this.editedActivity.set(null);
		// Don't clear originalTasks - keep them so currentTasks can return them
		// when components re-render after exiting edit mode
		this.editedTasks.set([]);
		this.hasChanges.set(false);
		this.presentTaskAdditionalSettingsComponent = null;
	}

	public updateEditedActivity (activity: Activity, bypassMarkAsChanged = false): void {
		if (!this.isEditMode) {
			return;
		}

		this.editedActivity.set(activity);

		if (!bypassMarkAsChanged) {
			this.markAsChanged();
		}
	}

	public updateOriginalActivity (activity: Activity, bypassMarkAsChanged = false): void {
		if (!this.isEditMode) {
			return;
		}

		this.originalActivity.set(activity);

		if (!bypassMarkAsChanged) {
			this.markAsChanged();
		}
	}

	public updateEditedTasks (tasks: Task[]): void {
		if (!this.isEditMode) {
			return;
		}

		this.editedTasks.set(tasks);
		this.markAsChanged();
	}

	public updateEditedTask (task: Task): void {
		if (!this.isEditMode) {
			throw new Error('Tried to update task while not in edit mode');
		}

		const tasks = this.editedTasks();
		const index = tasks.findIndex((t) => t.id === task.id);

		if (index === -1) {
			throw new Error(`Task with id ${task.id} not found in edited tasks`);
		}

		const updatedTasks = [...tasks];
		updatedTasks[index] = task;
		this.editedTasks.set(updatedTasks);
		this.markAsChanged();
	}

	public markAsChanged (): void {
		if (this.isEditMode) {
			this.hasChanges.set(true);
		}
	}

	public setIsSaving (isSaving: boolean): void {
		this.isSaving$$.next(isSaving);
	}

	public commitChanges (updatedActivity: Activity): void {
		if (updatedActivity) {
			this.selectedService.setActivity(this.activityModel.model(updatedActivity));
		}

		// Update originalTasks with the edited tasks so they persist after exiting edit mode
		// This ensures that components can still access the saved changes via getTask()
		// until the next navigation or edit mode entry
		const editedTasks = this.editedTasks();
		this.originalTasks.set(editedTasks);

		this.exitEditMode();
	}

	public revertChanges (): { activity: Activity | null; tasks: Task[] } {
		const originalActivity = this.originalActivity();
		const originalTasks = this.originalTasks();

		const result = {
			activity: originalActivity,
			tasks: [...originalTasks]
		};

		if (originalActivity) {
			this.selectedService.setActivity(this.activityModel.model(originalActivity));
		}

		this.changesReverted$$.next();

		this.exitEditMode();
		return result;
	}

	public confirmDiscardChanges (): Observable<boolean> {
		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: this.translate.instant('activity-settings_controller-unsaved-changes'),
				message: this.translate.instant('activity-settings_controller-message-2'),
				confirmText: this.translate.instant('common_discard-changes'),
				cancelText: this.translate.instant('common_cancel')
			},
			disableClose: true
		});

		return dialogRef.afterClosed().pipe(
			map((confirmed: boolean) => {
				if (confirmed) {
					this.revertChanges();
					return true; // allow navigation
				} else {
					return false; // prevent navigation
				}
			})
		);
	}

	public registerPresentTaskAdditionalSettingsComponent (component: any): void {
		this.presentTaskAdditionalSettingsComponent = component;
	}

	public validateAdditionalSettingsForm (): boolean {
		if (this.presentTaskAdditionalSettingsComponent) {
			return this.presentTaskAdditionalSettingsComponent.validateAdditionalSettingsForm();
		}
		return true;
	}
}
